// Transitions portées du thème Stretch, rejouées en Web Animations API (pas de librairie).
const SLIDESHOW_HERO_EASE = {
  verticalOut: "cubic-bezier(0.55, 0.085, 0.68, 0.53)",
  verticalIn: "cubic-bezier(0.19, 1, 0.22, 1)",
  curtain: "cubic-bezier(0.77, 0, 0.175, 1)",
  distortion: "cubic-bezier(0.645, 0.045, 0.355, 1)",
  perspectiveOut: "cubic-bezier(0.445, 0.05, 0.55, 0.95)",
};

// { transform: [a, b], opacity: [1, 0] } -> [{ transform: a, opacity: 1 }, { transform: b, opacity: 0 }]
function slideshowHeroKeyframes(properties) {
  return [0, 1].map((step) =>
    Object.fromEntries(
      Object.entries(properties).map(([property, values]) => [
        property,
        values[step],
      ]),
    ),
  );
}

if (!customElements.get("slideshow-hero")) {
  customElements.define(
    "slideshow-hero",
    class SlideshowHero extends HTMLElement {
      constructor() {
        super();
        this.index = 0;
        this.isTransitioning = false;
        this.animations = [];
        this.controller = new AbortController();
      }

      connectedCallback() {
        // Reconnexion (réordonnancement dans l'éditeur) : l'ancien controller est déjà abandonné.
        if (this.controller.signal.aborted) this.controller = new AbortController();

        this.slides = Array.from(
          this.querySelectorAll(".slideshow-hero__slide"),
        );
        this.markers = Array.from(
          this.querySelectorAll(".slideshow-hero__marker"),
        );
        this.autoplayButton = this.querySelector(".slideshow-hero__autoplay");
        this.autoplayDelay =
          (parseFloat(this.getAttribute("autoplay")) || 0) * 1000;
        this.pauseOnHover = this.hasAttribute("pause-on-hover");

        this.updateFillOffset();
        window.addEventListener("resize", () => this.updateFillOffset(), {
          signal: this.controller.signal,
          passive: true,
        });

        if (this.slides.length === 0) return;
        this.setActive(0);

        // Vaut aussi pour un slide unique : c'est ce qui lance la vidéo (rendue sans autoplay).
        this.observeVisibility();
        this.bindViewportVideoSync();

        if (this.slides.length > 1) {
          this.bindControls();
          this.bindSwipe();
          this.bindKeyboard();
          this.bindDesignMode();
        }
      }

      disconnectedCallback() {
        this.controller.abort();
        clearTimeout(this.leaveViewTimer);
        this.stopPlayer();
        this.observer?.disconnect();
        this.cancelAnimations();
      }

      // "fill" = plein écran : on retire ce qui précède le slideshow (header non transparent,
      // barre d'annonce). En header transparent la section remonte sous le header, l'offset est 0.
      // rAF : laisse page-header-image-banner poser sa marge négative avant de mesurer.
      updateFillOffset() {
        if (!this.classList.contains("slideshow-hero--fill")) return;
        requestAnimationFrame(() => {
          const offset = Math.max(
            0,
            this.getBoundingClientRect().top + window.scrollY,
          );
          this.style.setProperty("--slideshow-hero-fill-offset", `${offset}px`);
        });
      }

      get effect() {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          return "fade";
        }
        return this.getAttribute("transition") || "fade";
      }

      /* ---------------------------------------------------------------- navigation */

      next() {
        return this.select((this.index + 1) % this.slides.length, "next");
      }

      previous() {
        return this.select(
          (this.index - 1 + this.slides.length) % this.slides.length,
          "previous",
        );
      }

      async select(index, direction, { instant = false } = {}) {
        if (index === this.index || !this.slides[index]) return;
        // "fade" superpose les slides sans les déplacer, on peut donc l'interrompre.
        if (this.isTransitioning && this.effect !== "fade") return;

        const from = this.slides[this.index];
        const to = this.slides[index];
        direction ??= index > this.index ? "next" : "previous";

        this.cancelAnimations();
        this.stopPlayer();
        this.index = index;
        this.isTransitioning = true;

        to.classList.add("is-selected");
        this.updateMarkers();
        this.syncVideos(to);
        this.startPlayer(await this.durationForSlide(to));

        await this.animate(
          this.buildTransition(from, to, direction),
          instant,
        );

        from.classList.remove("is-selected");
        this.isTransitioning = false;
      }

      setActive(index) {
        this.slides.forEach((slide, i) =>
          slide.classList.toggle("is-selected", i === index),
        );
        this.updateMarkers();
      }

      updateMarkers() {
        this.markers.forEach((marker, i) =>
          marker.setAttribute("aria-current", i === this.index ? "true" : "false"),
        );
      }

      /* ---------------------------------------------------------------- animations */

      animate(specs, instant) {
        this.animations = specs.map(([element, properties, options]) =>
          element.animate(slideshowHeroKeyframes(properties), {
            fill: "forwards",
            ...options,
          }),
        );

        if (instant) this.animations.forEach((animation) => animation.finish());

        return Promise.all(
          this.animations.map((animation) =>
            animation.finished.catch(() => {}),
          ),
        ).then(() => this.cancelAnimations());
      }

      // Les états finaux sont des transforms identité : annuler ne laisse rien de figé.
      cancelAnimations() {
        this.animations.forEach((animation) => animation.cancel());
        this.animations = [];
      }

      buildTransition(from, to, direction) {
        const rtl = document.dir === "rtl";
        const flip = (direction === "next" ? 1 : -1) * (rtl ? -1 : 1);
        const isMobile = window.innerWidth < 700;
        const fromInner = from.firstElementChild;
        const toInner = to.firstElementChild;

        switch (this.effect) {
          case "vertical_reveal":
            return [
              [from, { transform: ["translateY(0)", `translateY(${flip * -100}%)`] }, { duration: 300, easing: SLIDESHOW_HERO_EASE.verticalOut }],
              [fromInner, { transform: ["translateY(0)", `translateY(${flip * 75}%)`] }, { duration: 300, easing: SLIDESHOW_HERO_EASE.verticalOut }],
              [to, { transform: [`translateY(${flip * 100}%)`, "translateY(0)"] }, { duration: 700, delay: 400, easing: SLIDESHOW_HERO_EASE.verticalIn }],
              [toInner, { transform: [`translateY(${flip * -75}%)`, "translateY(0)"] }, { duration: 700, delay: 400, easing: SLIDESHOW_HERO_EASE.verticalIn }],
            ];

          case "curtain": {
            const duration = isMobile ? 800 : 1000;
            const easing = SLIDESHOW_HERO_EASE.curtain;
            return [
              [from, { transform: ["translateX(0)", `translateX(${flip * -100}%)`] }, { duration, easing }],
              [fromInner, { transform: ["translateX(0)", `translateX(${flip * 30}%)`] }, { duration, easing }],
              [to, { transform: [`translateX(${flip * 100}%)`, "translateX(0)"] }, { duration, easing }],
              [toInner, { transform: [`translateX(${flip * -30}%)`, "translateX(0)"] }, { duration, easing }],
            ];
          }

          case "distortion": {
            const duration = isMobile ? 600 : 800;
            const easing = SLIDESHOW_HERO_EASE.distortion;
            fromInner.style.transformOrigin = direction === "next" ? "100% 50%" : "0% 50%";
            toInner.style.transformOrigin = direction === "next" ? "0% 50%" : "100% 50%";
            return [
              [from, { transform: ["translateX(0)", `translateX(${flip * -100}%)`] }, { duration, easing }],
              [fromInner, { transform: ["scaleX(1)", "scaleX(2.5)"] }, { duration, easing }],
              [to, { transform: [`translateX(${flip * 100}%)`, "translateX(0)"] }, { duration, easing }],
              [toInner, { transform: [`translateX(${flip * -100}%) scaleX(2.5)`, "translateX(0) scaleX(1)"] }, { duration, easing }],
            ];
          }

          case "perspective":
            return [
              [from, { transform: ["scale(1)", "scale(0.9)"], opacity: [1, 0.3] }, { duration: 150, easing: SLIDESHOW_HERO_EASE.perspectiveOut }],
              [from, { transform: ["translateY(0) scale(0.9)", "translateY(-20%) scale(0.9)"], opacity: [0.3, 0] }, { duration: 700, delay: 250, easing: SLIDESHOW_HERO_EASE.curtain }],
              [to, { transform: ["translateY(100%) scale(1)", "translateY(0) scale(1)"] }, { duration: 700, delay: 250, easing: SLIDESHOW_HERO_EASE.curtain }],
              [toInner, { transform: ["translateY(-50%)", "translateY(0)"] }, { duration: 700, delay: 250, easing: SLIDESHOW_HERO_EASE.curtain }],
            ];

          case "scale_down":
            return [
              [from, { opacity: [1, 0], transform: ["scale(1)", "scale(0.7)"] }, { duration: 500, easing: SLIDESHOW_HERO_EASE.distortion }],
              [to, { transform: [`translateY(${flip * 100}%)`, "translateY(0)"] }, { duration: 700, easing: SLIDESHOW_HERO_EASE.distortion }],
            ];

          default:
            return [
              [from, { opacity: [1, 0] }, { duration: 300 }],
              [to, { opacity: [0, 1] }, { duration: 300 }],
            ];
        }
      }

      /* ---------------------------------------------------------------- autoplay */

      // hero.liquid place mobile (desktop-hidden) avant desktop (mobile-hidden).
      // On suit le CSS réel (display) pour rester aligné avec le breakpoint, y compris
      // après resize / preview éditeur.
      activeVideo(slide) {
        if (!slide) return null;
        const videos = Array.from(slide.querySelectorAll("video"));
        if (videos.length <= 1) return videos[0] || null;

        return (
          videos.find((video) => getComputedStyle(video).display !== "none") ||
          videos[0]
        );
      }

      async durationForSlide(slide) {
        const video = this.activeVideo(slide);
        if (!video) return this.autoplayDelay;

        if (isNaN(video.duration)) {
          await new Promise((resolve) => {
            video.addEventListener("loadedmetadata", resolve, { once: true });
            setTimeout(resolve, 2000);
          });
        }
        return isNaN(video.duration) ? this.autoplayDelay : video.duration * 1000;
      }

      startPlayer(duration) {
        this.stopPlayer();
        if (this.slides.length < 2) return;
        if (!this.autoplayDelay || this.isPaused || !duration) return;

        this.playerDuration = duration;
        this.playerStartedAt = performance.now();
        this.playerTimer = setTimeout(() => this.next(), duration);

        const progress = this.markers[this.index]?.querySelector(
          ".slideshow-hero__marker-progress",
        );
        this.progressAnimation = progress?.animate(
          [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
          { duration, easing: "linear", fill: "forwards" },
        );
      }

      stopPlayer() {
        clearTimeout(this.playerTimer);
        this.progressAnimation?.cancel();
        this.progressAnimation = null;
      }

      // La vidéo se met en pause indépendamment du timer de rotation, qui n'existe pas à 1 slide.
      pausePlayer() {
        this.activeVideo(this.selectedSlide)?.pause();
        if (!this.playerTimer) return;
        clearTimeout(this.playerTimer);
        this.playerTimer = null;
        this.playerDuration -= performance.now() - this.playerStartedAt;
        this.progressAnimation?.pause();
      }

      resumePlayer() {
        if (this.isPaused) return;
        this.activeVideo(this.selectedSlide)?.play().catch(() => {});
        if (this.playerTimer || !this.playerDuration) return;
        this.playerStartedAt = performance.now();
        this.playerTimer = setTimeout(() => this.next(), this.playerDuration);
        this.progressAnimation?.play();
      }

      get selectedSlide() {
        return this.slides[this.index];
      }

      syncVideos(slide) {
        const active = this.activeVideo(slide);
        this.querySelectorAll("video").forEach((video) => {
          if (video !== active) video.pause();
        });
        if (!active) return;
        try {
          active.currentTime = 0;
        } catch (_) {
          /* ignore NotSupportedError tant que les métadonnées ne sont pas prêtes */
        }
        active.play().catch(() => {});
      }

      // Recalage si on croise le breakpoint desktop/mobile (preview, rotation, resize).
      bindViewportVideoSync() {
        const query = window.matchMedia("(max-width: 899px)");
        const onChange = () => {
          if (!this.isInView) return;
          this.syncVideos(this.selectedSlide);
        };
        query.addEventListener("change", onChange);
        this.controller.signal.addEventListener(
          "abort",
          () => query.removeEventListener("change", onChange),
          { once: true },
        );
      }

      /* ---------------------------------------------------------------- listeners */

      bindControls() {
        const { signal } = this.controller;

        this.markers.forEach((marker, i) =>
          marker.addEventListener("click", () => this.select(i), { signal }),
        );

        this.autoplayButton?.addEventListener(
          "click",
          () => {
            this.isPaused = !this.isPaused;
            this.autoplayButton.setAttribute("aria-pressed", String(this.isPaused));
            this.classList.toggle("slideshow-hero--paused", this.isPaused);
            if (this.isPaused) this.pausePlayer();
            else {
              this.playerDuration ||= this.autoplayDelay;
              this.resumePlayer();
            }
          },
          { signal },
        );

        if (this.pauseOnHover) {
          this.addEventListener("mouseenter", () => this.pausePlayer(), { signal });
          this.addEventListener("mouseleave", () => this.resumePlayer(), { signal });
        }
      }

      bindSwipe() {
        const { signal } = this.controller;
        let startX = null;

        this.addEventListener(
          "pointerdown",
          (event) => {
            startX = event.pointerType === "mouse" ? null : event.clientX;
          },
          { signal, passive: true },
        );

        this.addEventListener(
          "pointerup",
          (event) => {
            if (startX === null) return;
            const delta = event.clientX - startX;
            startX = null;
            if (Math.abs(delta) < 50) return;
            const forward = document.dir === "rtl" ? delta > 0 : delta < 0;
            forward ? this.next() : this.previous();
          },
          { signal, passive: true },
        );
      }

      bindKeyboard() {
        this.tabIndex = 0;
        this.addEventListener(
          "keydown",
          (event) => {
            if (event.target !== this) return;
            if (event.code === "ArrowLeft") this.previous();
            else if (event.code === "ArrowRight") this.next();
          },
          { signal: this.controller.signal },
        );
      }

      bindDesignMode() {
        if (!Shopify.designMode) return;
        const { signal } = this.controller;
        const section = this.closest(".shopify-section") || this;

        section.addEventListener(
          "shopify:block:select",
          (event) => {
            const index = this.slides.indexOf(
              event.target.closest(".slideshow-hero__slide"),
            );
            if (index >= 0) this.select(index, null, { instant: event.detail.load });
            this.pausePlayer();
          },
          { signal },
        );

        section.addEventListener("shopify:block:deselect", () => this.resumePlayer(), {
          signal,
        });
      }

      // Pas d'autoplay ni de préchargement tant que la section n'est pas visible.
      // threshold 0 + debounce sortie : évite play→pause sur resize barre d'URL mobile
      // ou décalage de marge du header transparent.
      observeVisibility() {
        this.isInView = false;
        this.observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              clearTimeout(this.leaveViewTimer);
              this.isInView = true;
              this.preloadNeighbours();
              this.syncVideos(this.selectedSlide);
              this.durationForSlide(this.selectedSlide).then((duration) => {
                if (this.isInView) this.startPlayer(duration);
              });
            } else {
              clearTimeout(this.leaveViewTimer);
              this.leaveViewTimer = setTimeout(() => {
                this.isInView = false;
                this.pausePlayer();
              }, 150);
            }
          }
        }, { threshold: 0 });
        this.observer.observe(this);
      }

      preloadNeighbours() {
        const neighbours = [
          this.slides[(this.index - 1 + this.slides.length) % this.slides.length],
          this.selectedSlide,
          this.slides[(this.index + 1) % this.slides.length],
        ];
        neighbours.forEach((slide) => {
          slide
            ?.querySelectorAll('img[loading="lazy"]')
            .forEach((img) => img.removeAttribute("loading"));
        });
      }
    },
  );
}
